import { call, put, select, take } from 'redux-saga/effects';
import { delay } from 'redux-saga';

import {
    apiAction,
    buildUrl,
    get,
    getPathWithQueryString,
    isTokenExpired,
    logMessage,
    mapStateToArgs,
    pageContext,
    removeQueryString
} from 'ctm-util-functions';

import { splitTrafficIdSelector } from 'ctm-selectors';
import * as api from '../../api';
import { reporterLog } from '../reporter';
import { getAccessToken, getRefreshToken, setTokens, getCASessionToken } from './tokens';

import { getAuthConfig } from '../state_selectors';

/**
 * Process the request set on the requestAction property and its actions
 * (i.e. AuthenticationToken, PreRequestActions, ResponseActions, OnNextActions).
 * @param {Object} action          - The dispatched action that contains the description of how to post the payload
 * @param {Object} requestPayload  - The payload for the request.
 * @returns {Boolean}              - True when the processPayload generator has completed the tasks.
 */
export const processRequestAndActionsSaga = function* (action, requestPayload = null) {
    // If preRequestActions then Process preRequestActions
    yield call(_processPreRequestActions, action);

    // Get Authentication Tokens from state
    const accessToken = yield call(_getAuthenticationToken, action);

    // Get customer account session token
    const sessionToken = yield call(getCASessionToken, action.verticalStateKey);

    // Process the API request using the Payload. The payload is sent to the back end.
    const responsePayload = yield call(_processApiRequest, action, accessToken, requestPayload, sessionToken);
    if (action.responseFunc) {
        action.responseFunc(responsePayload);
    }

    // Single action succeeded, let everyone know by dispatching a status update.
    yield call(_setActionSuccess, action);

    // Process Actions on response (if any). Used to set the returned payload to the application state.
    yield call(_processResponseActions, action, responsePayload);

    // Process the onNext action (if any).
    yield call(processOnNextActions, action);

    return true;
};

/**
 * Dispatch the actions set before the main API Request.
 * @param {Object} action        - The action that describes the post payload request.
 */
const _processPreRequestActions = function* (action) {
    if (action.apiSchema.preRequestActions) {
        for (const requestAction of action.apiSchema.preRequestActions) {
            // @todo: this is basic and needs to be expanded for other use cases
            yield put({ ...requestAction });
        }
    }
};

/**
 * Get Authentication Tokens to be used in the request header.
 * Get a new token if none auth token is set in the state or the current one is expired.
 *
 * @param {Object} action - The action that describes the request.
 * @returns {String} Auth token required by API.
 * @throws {Error} When an API failure is detected.
 */
const _getAuthenticationToken = function* (action) {
    const accessToken = yield call(getAccessToken);
    const refreshToken = yield call(getRefreshToken);
    const pageState = pageContext.getProps().pageState || {};
    const settings = pageContext.getProps().models.pageSchemaModel.getSettings();
    const authApiSettings = get(settings, 'api.auth', null);

    // If no or expired accessToken, go get new tokens.
    if (!accessToken || isTokenExpired(accessToken)) {
        const renewToken = refreshToken && !isTokenExpired(refreshToken) ? refreshToken : null;

        // Make API request to fetch tokens.
        try {
            yield call(_processAuthTokenRequest, action, renewToken);
        } catch (error) {
            // Create a custom error so processSagaWithRetries() can detect token failure.
            // Can't just throw the object due to linting errors.
            const errorHandler = {
                ...error,
                action: apiAction(authApiSettings, pageState),
                verticalStateKey: action.verticalStateKey
            };
            throw errorHandler;
        }
    }

    return yield call(getAccessToken);
};

/**
 * Generator used to the request a new AuthToken or a refreshed one.
 * @param {Object} action - The dispatched action that contains the description of how to get the payload.
 * @param {String} refreshToken - The refresh token set in the old Authentication details.
 */
const _processAuthTokenRequest = function* (action, refreshToken = null) {
    const startTime = Date.now();
    const authConfig = yield select(getAuthConfig, action.verticalStateKey);

    const apiSchema = refreshToken
        ? { ...authConfig, ...authConfig.refresh, restArgs: [] } // Override default auth schema with refresh schema
        : authConfig;

    // Make the API request to get the Auth Payload.
    yield call(logMessage, 'Get Auth Payload');
    const restArgs = yield select(mapStateToArgs, apiSchema.restArgs);
    const authPayload = yield call(api.getAuthTokens,
        buildUrl(apiSchema, null, restArgs),
        refreshToken);

    // Log the response on the console and in the server.
    yield call(_receivedPayloadLog, action, startTime, authPayload, 'Get Auth Payload Finished');

    // Set tokens.
    yield call(setTokens, authPayload);
};

export const saveOfferDetails = (payload) => ({
    type: 'SET_OFFER_DETAILS',
    payload
});

export const offerShow = () => ({
    type: 'OFFER_SHOWN'
});

export const elevenMonthQuoteStart = () => ({
    type: 'ELEVEN_MONTH_QUOTE_START'
});

export const elevenMonthResults = () => ({
    type: 'ELEVEN_MONTH_RESULTS'
});

/**
 * Log the received payload.
 * @param {Object} action        - The action that describes the request.
 * @param {Number} startTime     - The start time stamp
 * @param {Object} payload       - The received payload
 * @param {String} message       - The message to be logged.
 */
const _receivedPayloadLog = function* (action, startTime, payload, message) {
    // Log using the reporter
    yield call(reporterLog, action, {
        message,
        timeSpent: Date.now() - startTime
    }, 'DEBUG');

    if (payload.offers) {
        yield put(saveOfferDetails(payload.offers));
    }
    // Log in the console.
    yield call(logMessage, 'Received Payload:', payload);
};

/**
 * Send the request to the API. It can be a GET or POST request
 * @param {Object} action - The action that describes the post payload request.
 * @param {Object} action.apiSchema - API schema.
 * @param {Object} action.apiSchema.type - GET or POST.
 * @param {String} accessToken - Auth token required by API.
 * @param {Object} requestPayload - The payload for the request.
 * @param {String} sessionToken - Customer account session token
 * @returns {Object} The response payload.
 */
const _processApiRequest = function* (action, accessToken, requestPayload = null, sessionToken) {
    const type = get(action, 'apiSchema.type', 'GET');
    const fetchOptions = {
        GET: api.getRequest,
        POST: api.postRequest
    };
    const fetchMethod = fetchOptions[type];

    // Log before fetching the request
    yield call(logMessage, `${type} Payload request:`, action.url, requestPayload);

    // Prepare custom headers if present
    let customHeaders = {};
    if (action.requestHeaders) {
        customHeaders = action.requestHeaders;
    }

    // Fetch the API request
    const startTime = Date.now();
    const payload = yield call(fetchMethod, action.url, accessToken, sessionToken, requestPayload, customHeaders);

    if (payload.status === 204) {
        /* The request executed properly but we did not get any content back
        which helps us differentiate between errors that don't affect the journey and meaningful errors */
        throw Error('Request succeeded but no content was returned');
    }

    // Log the received payload
    yield call(_receivedPayloadLog, action, startTime, payload.data, `${type} Payload request finished`);

    return payload.data;
};

/**
 * Dispatch an action informing that a individual action has completed successfully.
 * @param {Object} action - Redux action.
 * @param {String} action.type - Redux action type.
 */
const _setActionSuccess = function* ({ type }) {
    yield put({ type: `${type}_SUCCESS` });
};

/**
 * Process Actions on the API response (if any).
 * @param {Object} action        - The action that describes the post payload request.
 * @param {Object} payload       - the payload used for the response actions.
 */
const _processResponseActions = function* (action, payload) {
    const { apiSchema } = action;
    const splitTrafficId = yield select(splitTrafficIdSelector);
    if (apiSchema.responseActions) {
        for (const responseAction of apiSchema.responseActions) {
            responseAction.splitTrafficId = splitTrafficId;
            const actionPayload = _createActionPayload(responseAction, payload);
            if (!actionPayload) {
                continue;
            }
            yield put({ ...responseAction, ...actionPayload });
        }
    }
};

/**
 * Process the OnNext Action (there can only be one).
 * @param {Object} action - The action that describes the requested validation and progression.
 * @param {Boolean} transitPage - indicates whether or not we are transiting to a new page
 */
export const processOnNextActions = function* (action, transitPage = false) {
    const apiSchema = get(action, 'apiSchema.onNext');
    const pageState = pageContext.getProps().pageState;
    if (apiSchema) {
        if (apiSchema.condition) {
            const selector = (state) => state.getIn(apiSchema.condition.stateKey.split('.'));
            const stateValue = yield select(selector);
            if (stateValue !== apiSchema.condition.equals) {
                return;
            }
        }
        if (transitPage) {
            // Set the page state to transitioning
            yield put({
                type: 'SET_PAGE_VALUE',
                identifier: 'transitioningToNewPage',
                value: true
            });
        }

        // Map the application state to queryString arguments set in the schema.
        if (apiSchema.queryStringArgs) {
            apiSchema.queryStringValues = yield select(mapStateToArgs, apiSchema.queryStringArgs);
        }
        // Map the application state to rest arguments set in the schema.
        if (apiSchema.restArgs) {
            apiSchema.restArgs = yield select(mapStateToArgs, apiSchema.restArgs);
        }

        // Dispatch onNext Api Action
        yield put(apiAction(apiSchema, pageState, apiSchema.restArgs, apiSchema.baseUrl));

        // Use this if you want to block the responseRedirects from occurring, until redirectAfterAction has been dispatched.
        if (apiSchema.redirectAfterAction) {
            yield take(apiSchema.redirectAfterAction);
        }
        // apiSchema.redirectResponse = progressionModel.getNextPageLink({}, pageSchemaModel.getUrlQualifier());
        // If onNext contains a ajax call, we normally don't wait until the payload comes back before sending user to the next route.
        // However, we still can wait if we specify a response action (JOURNEY_CAN_PROGRESS) in the schema so we can pause the saga.
        // It will resume when 'JOURNEY_CAN_PROGRESS' is dispatched
        if (apiSchema.responseActions) {
            for (const responseAction of apiSchema.responseActions) {
                if (responseAction.type === 'JOURNEY_CAN_PROGRESS') {
                    action.eventCB();
                    yield take(responseAction.type);
                    break;
                }
            }
        }
    }
};

/**
 * @todo check when we can get rid of this. We shouldn't need to do this
 * Process the request and actions. Retry by default 5 times if any error.
 * @param {Function} processSaga  - Generator/Saga with the steps to follow for the request and actions.
 * @param {Object} action         - The action that describes the request.
 * @param {Object} requestPayload - The payload for the request.
 * @param {Number} retries        - Number of times to replay the request if it fails.
 * @returns {Boolean}             - Return to break the for loop.
 */
export const processSagaWithRetries = function* (processSaga, action, requestPayload, retries = 5) {
    const startTime = Date.now();
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < retries; i++) {
        try {
            return yield call(processSaga, action, requestPayload);
        } catch (error) {
            // Because here it can be any error (FE or BE response),
            // we want to make sure, 1) it is a BE response error, 2) it is a 5xx error before we retries
            const responseStatus = get(error, 'response.status');
            const isServerError = /^5[0-9]{2}$/.test(responseStatus);

            if (isServerError && i < retries && !action.apiSchema.skipRetryOnError) {
                // If there was an server side error, retry this postPayload another 4 times, 2 seconds apart
                yield call(delay, 2000);
            }
            if (!isServerError || i === retries - 1 || action.apiSchema.skipRetryOnError) {
                // Reset transition state so it frees up all the disabled fields;
                yield put({
                    type: 'SET_PAGE_VALUE',
                    identifier: 'transitioningToNewPage',
                    value: false
                });
                // Extract failed action (from error if available).
                const failedAction = error.action || action;
                // Log the Payload Error using the reporter.
                yield call(logPayloadError, error, failedAction, startTime);
                // Process the actions on error.
                yield call(processErrorActions, error, failedAction);
                // Call errorFunc if present
                if (typeof failedAction.errorFunc === 'function') {
                    failedAction.errorFunc(error);
                }
                yield call(processRedirect, action, 'redirectOnError');
                // break for client error
                break;
            }
        }
    }

    // If we got this far, the request has failed.
    return false;
};

/**
 * Log the Payload Error.
 * @param {Object} error         - The error to be passed to the action.
 * @param {Object} action        - The action to be passed to the action.
 * @param {Number} startTime     - The startTime used for the total time spent.
 */
export const logPayloadError = function* (error, action, startTime) {
    // Adding the time spent info in the error
    Object.assign(error, { timeSpent: Date.now() - startTime });

    // Dispatch the action to set the error details in the Redux store
    if (action.apiSchema.failureType === 'fatal') {
        yield put(_createErrorAction(error));
    }

    // Run the reporter
    yield [
        // Log the error using the reporter.
        call(reporterLog, action, error),
    ];
};

/**
 * Return an action for setting errors on state
 * @param  {Object} errorPayload - The error payload
 * @returns {Object}             - An action that can be used to set the error on state
 */
const _createErrorAction = (errorPayload) => {
    return { type: 'SET_CURRENT_ERROR', errorPayload };
};

/**
 * Process Actions on API Error (if any).
 * @param {Object} error         - The error to be passed to the action.
 * @param {Object} action        - The action that describes the post payload request.
 */
export const processErrorActions = function* (error, action) {
    const { apiSchema, verticalStateKey } = action;
    if (apiSchema.errorActions) {
        const splitTrafficId = yield select(splitTrafficIdSelector);
        for (const errorAction of apiSchema.errorActions) {
            const actionPayload = _createActionPayload(errorAction);
            errorAction.splitTrafficId = splitTrafficId;
            // eslint-disable-next-line prefer-object-spread
            const errorPayload = Object.assign({}, error, errorAction.errorPayload);
            yield put({
                ...errorAction,
                ...actionPayload,
                errorPayload,
                verticalStateKey
            });
        }
    }
};

// Set of simple eval functions to a value extracted from a payload
// We may not need any more of these?
// @todo: investigate if our existing transforms can handle this case
const PAYLOAD_VALUE_EVALS = {
    isNotEmpty: (value) => !!value && value.length > 0,
    isEmpty: (value) => value === undefined || value.length === 0,
    isTrue: (value) => value
};

/**
 * Create a payload from API response that forms part of an Action.
 * Extracting values from the payload is currently ONLY used to set postCodeSupportedElec, postCodeSupportedGas, tariff and resetPCNSModal in Energy
 * In these cases all except for the tariff can be sent in a single action, instead 3 separate actions. This function
 * gathers the required identifiers and values from the action config defined in the schema, from the API response payload
 *
 * Other actions will already have their payload specified correctly, and this function will just return that payload
 * @param {Object} responseAction - The responseAction schema.
 * @param {Object} payload - The API response payload.
 * @returns {Object} The action payload that can form part of an Action
 */
const _createActionPayload = (responseAction = {}, payload) => {
    if (responseAction.args) {
        // @todo: test this :P
        // eslint-disable-next-line no-console
        console.warn(
            `A responseAction that still uses "args" was received, these actions have now been deprecated, 
            please update your responseActions to not have nested args, see E2E Energy quote form schema for examples`,
            responseAction
        );
        return responseAction.args;
    }

    if (!responseAction.valuesFromPayload && !responseAction.valueFromPayload && !responseAction.valueCondition) {
        return { payload };
    }

    if (responseAction.valueFromPayload) {
        if (responseAction.valueFromPayloadCondition) {
            const [evalName, payloadId] = responseAction.valueFromPayloadCondition.split(':');
            const payloadValue = get(payload, payloadId);
            if (PAYLOAD_VALUE_EVALS[evalName](payloadValue)) {
                return { value: get(payload, responseAction.valueFromPayload) };
            }
            return { value: '' };
        }
        return { value: get(payload, responseAction.valueFromPayload) };
    }

    if (responseAction.valueCondition) {
        const [evalName, payloadId] = responseAction.valueCondition.split(':');
        const payloadValue = get(payload, payloadId);
        if (PAYLOAD_VALUE_EVALS[evalName](payloadValue)) {
            return { value: responseAction.value };
        }
        return null;
    }

    // Only used to extract values for postCodeSupportedElec, postCodeSupportedGas, resetPCNSModal
    const values = responseAction.valuesFromPayload.reduce((actionValues, actionConfig) => {
        if (actionConfig.valueConfig) {
            const [evalName, payloadId] = actionConfig.valueConfig.split(':');
            const payloadValue = get(payload, payloadId);
            const value = PAYLOAD_VALUE_EVALS[evalName](payloadValue);
            actionValues.push({
                identifier: actionConfig.identifier,
                value
            });
        } else if (actionConfig.value) {
            // Only for resetPCNSModal which has a static value
            actionValues.push(actionConfig);
        }
        return actionValues;
    }, []);

    return { values };
};

/**
 * Process redirects set in the action API schema.
 * @param {Object} action        - The action that describes the payload request.
 * @param {String} actionPath    - The action object path to get the redirect url
 */
export const processRedirect = function* (action, actionPath) {
    const redirectValue = get(action, `apiSchema.${actionPath}`, false);
    if (redirectValue) {
        yield call(logMessage, 'Redirecting:', redirectValue);
        if (action.eventCB) {
            // @todo: I'm not sure this should be called here, need to check a few things - see EVT-1505
            action.eventCB();
        }
        const browserHistory = pageContext.getProps().browserHistory;
        yield call(() => {
            // isEdit=true is added through manual edit link and should be removed when progress the journey
            // TODO: should be platform wide but currently only used in car, but I had to add it here as the journey progress is in platform, check the usage
            const urlWithParams = getPathWithQueryString(redirectValue);
            const url = removeQueryString(urlWithParams, 'isEdit=true');
            // replaceHistory: true means the current page (e.g. loading/calculating page) should not appear in browser history
            if (action.replaceHistory) {
                browserHistory.replace(url);
            } else {
                browserHistory.push(url);
            }
        });
    }
};
